import { useEffect, useRef } from "react";
import { getPerf, onPerf, AURORA_SCALE } from "../../lib/perf";

// The welcome: a LITERAL ocean. A raymarched heightfield sea (choppy-wave
// octaves, geometric tracing, analytic-ish normals) under a moonlit night sky,
// with fresnel reflection, subsurface teal in the wave flanks, a moon-glint
// path, crest foam - and a real wave-equation ripple simulation projected onto
// the 3D surface so touching the screen disturbs the water where you touch.
// Raw WebGL2 (no Three payload); tier-scaled; ?shot=1 enables readback for
// screenshot-driven iteration; reduced-motion gets a still frame.

const VERT = `attribute vec2 a; void main(){ gl_Position=vec4(a,0.,1.); }`;

// Interactive ripple sim (2D wave equation, R=height, G=previous).
const SIM_FRAG = `
precision highp float;
uniform sampler2D u_prev;
uniform vec2 u_texel;
uniform vec4 u_imp[8];
void main(){
  vec2 uv = gl_FragCoord.xy * u_texel;
  float l = texture2D(u_prev, uv - vec2(u_texel.x, 0.)).r;
  float r = texture2D(u_prev, uv + vec2(u_texel.x, 0.)).r;
  float t = texture2D(u_prev, uv - vec2(0., u_texel.y)).r;
  float b = texture2D(u_prev, uv + vec2(0., u_texel.y)).r;
  vec2 hp = texture2D(u_prev, uv).rg;
  float next = (l + r + t + b) * 0.5 - hp.g;
  next *= 0.982;
  for (int i = 0; i < 8; i++) {
    vec2 dp = uv - u_imp[i].xy;
    next += u_imp[i].w * exp(-dot(dp, dp) / max(u_imp[i].z, 1e-6));
  }
  float edge = smoothstep(0., .04, uv.x) * smoothstep(0., .04, 1. - uv.x)
             * smoothstep(0., .04, uv.y) * smoothstep(0., .04, 1. - uv.y);
  next *= mix(0.9, 1.0, edge);
  gl_FragColor = vec4(clamp(next, -3., 3.), hp.r, 0., 1.);
}`;

// The ocean.
const OCEAN_FRAG = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_quality;   // 1 high, 0.6 med, 0.3 low
uniform sampler2D u_ripple;
uniform vec4 u_patch;      // world->sim mapping: x0, z0, width, depth
uniform float u_hero;       // 0 welcome defaults, 1 contained app hero
uniform float u_theme;      // 0 dark, 1 light
uniform float u_hue;        // cool records -> warm today/person
uniform float u_intensity;  // visual contrast/specular strength
uniform float u_amplitude;  // wave height multiplier

const float CAMY = 3.4;
const float PITCH = 0.16;
const float FOV = 0.75;

float hash(vec2 p){ float h = dot(p, vec2(127.1, 311.7)); return fract(sin(h) * 43758.5453123); }
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f*f*(3.0-2.0*f);
  return -1.0 + 2.0*mix(mix(hash(i), hash(i+vec2(1.,0.)), u.x), mix(hash(i+vec2(0.,1.)), hash(i+vec2(1.,1.)), u.x), u.y);
}

// the classic choppy ocean octave
float seaOctave(vec2 uv, float choppy){
  uv += noise(uv);
  vec2 wv = 1.0 - abs(sin(uv));
  vec2 swv = abs(cos(uv));
  wv = mix(wv, swv, wv);
  return pow(1.0 - pow(wv.x * wv.y, 0.65), choppy);
}

const mat2 OCT = mat2(1.6, 1.2, -1.2, 1.6);

float rippleAt(vec2 xz){
  vec2 suv = (xz - u_patch.xy) / u_patch.zw;
  if (suv.x <= 0.01 || suv.x >= 0.99 || suv.y <= 0.01 || suv.y >= 0.99) return 0.0;
  // fade ripple influence with distance so the horizon stays pristine
  float fade = 1.0 - smoothstep(0.45, 1.0, suv.y);
  return texture2D(u_ripple, suv).r * 0.22 * fade;
}

// coarse height for ray tracing (no ripple, fewer octaves)
float mapCoarse(vec3 p){
  float heroAmp = mix(1.0, clamp(u_amplitude, 0.20, 1.10), u_hero);
  float lightBite = u_hero * u_theme * 0.55;
  float freq = 0.16, amp = 0.62 * heroAmp, choppy = 4.0 + lightBite;
  vec2 uv = p.xz; uv.x *= 0.75;
  float t = 1.0 + u_time * 0.8;
  float h = 0.0;
  for (int i = 0; i < 3; i++) {
    float d = seaOctave((uv + t) * freq, choppy) + seaOctave((uv - t) * freq, choppy);
    h += d * amp;
    uv = OCT * uv;
    freq *= 1.9; amp *= 0.22;
    choppy = mix(choppy, 1.0, 0.2);
  }
  return p.y - h;
}

// detailed height for shading (more octaves + interactive ripples)
float mapDetail(vec3 p){
  float heroAmp = mix(1.0, clamp(u_amplitude, 0.20, 1.10), u_hero);
  float lightBite = u_hero * u_theme * 0.55;
  float freq = 0.16, amp = 0.62 * heroAmp, choppy = 4.0 + lightBite;
  vec2 uv = p.xz; uv.x *= 0.75;
  float t = 1.0 + u_time * 0.8;
  float h = 0.0;
  int iters = u_quality > 0.55 ? 5 : 4;
  for (int i = 0; i < 5; i++) {
    if (i >= iters) break;
    float d = seaOctave((uv + t) * freq, choppy) + seaOctave((uv - t) * freq, choppy);
    h += d * amp;
    uv = OCT * uv;
    freq *= 1.9; amp *= 0.22;
    choppy = mix(choppy, 1.0, 0.2);
  }
  return p.y - h - rippleAt(p.xz);
}

vec3 getNormal(vec3 p, float eps){
  vec3 n;
  n.y = mapDetail(p);
  n.x = mapDetail(vec3(p.x + eps, p.y, p.z)) - n.y;
  n.z = mapDetail(vec3(p.x, p.y, p.z + eps)) - n.y;
  n.y = eps;
  return normalize(n);
}

float heightMapTracing(vec3 ori, vec3 dir, out vec3 p){
  float tm = 0.0;
  float tx = 60.0;
  float hx = mapCoarse(ori + dir * tx);
  if (hx > 0.0) { p = ori + dir * tx; return tx; }
  float hm = mapCoarse(ori);
  for (int i = 0; i < 8; i++) {
    float tmid = mix(tm, tx, hm / (hm - hx));
    p = ori + dir * tmid;
    float hmid = mapCoarse(p);
    if (hmid < 0.0) { tx = tmid; hx = hmid; }
    else { tm = tmid; hm = hmid; }
  }
  return mix(tm, tx, hm / (hm - hx));
}

// Bordeaux night sky: dark wine zenith, bordeaux horizon, gold moon-glow.
// (pre-normalized: GLSL ES 1.00 globals need constant initializers)
const vec3 SUN_DIR = vec3(0.1520, 0.2230, 0.9628);

vec3 skyColor(vec3 dir){
  float y = max(dir.y, 0.0);
  // Bordeaux brand gradient: dark wine sky into a saturated horizon.
  vec3 zen = vec3(0.028, 0.010, 0.018);
  vec3 hor = vec3(0.306, 0.063, 0.114);
  if (u_hero > 0.5) {
    float warm = smoothstep(0.48, 0.68, u_hue);
    vec3 heroZenDark = mix(vec3(0.018, 0.010, 0.022), vec3(0.036, 0.010, 0.018), warm);
    vec3 heroHorDark = mix(vec3(0.176, 0.048, 0.122), vec3(0.352, 0.058, 0.096), warm);
    vec3 heroZenLight = mix(vec3(0.046, 0.015, 0.034), vec3(0.070, 0.014, 0.025), warm);
    vec3 heroHorLight = mix(vec3(0.288, 0.052, 0.132), vec3(0.456, 0.064, 0.106), warm);
    zen = mix(heroZenDark, heroZenLight, u_theme);
    hor = mix(heroHorDark, heroHorLight, u_theme);
  }
  vec3 sky = mix(hor, zen, pow(y, 0.5));
  // moon/sun glint: cream disc, tight gold glow, broad warm haze.
  float sd = dot(dir, SUN_DIR);
  float disc = smoothstep(0.9997, 0.99988, sd);
  float glow = pow(max(sd, 0.0), 360.0) * 0.9 + pow(max(sd, 0.0), 36.0) * 0.18;
  float haze = pow(max(sd, 0.0), 6.0) * 0.08;
  float heroBoost = mix(1.0, 0.82 + u_intensity * 0.34 + u_theme * 0.14, u_hero);
  sky += vec3(0.949, 0.914, 0.839) * disc * 3.2 * heroBoost;
  sky += vec3(0.839, 0.745, 0.478) * glow * heroBoost;
  sky += vec3(0.627, 0.173, 0.267) * haze * heroBoost;
  // soft drifting cirrus
  float cl = noise(vec2(dir.x * 3.0 + u_time * 0.01, dir.y * 8.0 + dir.z * 2.0)) * 0.5 + 0.5;
  sky += vec3(0.949, 0.914, 0.839) * smoothstep(0.62, 0.95, cl) * 0.07 * smoothstep(0.04, 0.4, y);
  return sky;
}

vec3 seaColor(vec3 p, vec3 n, vec3 eye, vec3 dir, float dist){
  // Seascape-class Bordeaux water.
  vec3 SEA_BASE = vec3(0.306, 0.063, 0.114);
  vec3 SEA_WATER = vec3(0.549, 0.122, 0.184);
  vec3 SUBSURFACE = vec3(0.627, 0.173, 0.267);
  if (u_hero > 0.5) {
    float warm = smoothstep(0.48, 0.68, u_hue);
    vec3 baseDark = mix(vec3(0.114, 0.026, 0.078), vec3(0.235, 0.036, 0.066), warm);
    vec3 waterDark = mix(vec3(0.360, 0.074, 0.160), vec3(0.560, 0.095, 0.128), warm);
    vec3 subDark = mix(vec3(0.520, 0.128, 0.240), vec3(0.690, 0.145, 0.185), warm);
    vec3 baseLight = mix(vec3(0.126, 0.030, 0.088), vec3(0.280, 0.036, 0.064), warm);
    vec3 waterLight = mix(vec3(0.420, 0.078, 0.178), vec3(0.650, 0.104, 0.132), warm);
    vec3 subLight = mix(vec3(0.620, 0.130, 0.272), vec3(0.820, 0.150, 0.186), warm);
    SEA_BASE = mix(baseDark, baseLight, u_theme);
    SEA_WATER = mix(waterDark, waterLight, u_theme);
    SUBSURFACE = mix(subDark, subLight, u_theme);
  }
  float fresnel = clamp(1.0 - dot(n, -dir), 0.0, 1.0);
  fresnel = pow(fresnel, 3.0) * 0.65;

  vec3 reflectDir = reflect(dir, n);
  reflectDir.y = abs(reflectDir.y); // sea reflects sky
  vec3 reflected = skyColor(reflectDir);
  float diffuse = pow(dot(n, SUN_DIR) * 0.4 + 0.6, 80.0) * 0.12;
  vec3 refracted = SEA_BASE + diffuse * SEA_WATER;

  vec3 color = mix(refracted, reflected, fresnel);
  // height-tinted body color: wave flanks pick up cherry and wine light.
  float atten = max(1.0 - dist * dist * 0.001, 0.0);
  color += SUBSURFACE * (p.y - 0.55) * mix(0.18, 0.24 + u_theme * 0.08, u_hero) * atten;

  // sun specular path + glitter sparkle (noise-broken micro-facets)
  float rs = max(dot(reflectDir, SUN_DIR), 0.0);
  float spec = pow(rs, 40.0) * 0.7 + pow(rs, 320.0) * 2.6;
  float glitter = smoothstep(0.45, 1.0, noise(p.xz * 16.0 + u_time * 2.4));
  float sparkle = mix(1.0, 0.74 + u_intensity * 0.48 + u_theme * 0.22, u_hero);
  color += vec3(0.839, 0.745, 0.478) * spec * (0.6 + 1.3 * glitter) * sparkle;

  // crest foam: bright white where the wave breaks
  float crest = smoothstep(0.74, 1.02, p.y + noise(p.xz * 6.0 + u_time) * 0.10);
  color += vec3(0.949, 0.914, 0.839) * crest * mix(0.42, 0.32 + u_intensity * 0.18, u_hero) * atten;

  // touch-ripple shimmer: rings catch the sun
  float rip = abs(rippleAt(p.xz));
  color += vec3(0.949, 0.914, 0.839) * rip * 1.8 * atten;

  return color;
}

void main(){
  vec2 uv = (gl_FragCoord.xy / u_res.xy) * 2.0 - 1.0;
  uv.x *= u_res.x / u_res.y;

  vec3 ori = vec3(0.0, CAMY, 0.0);
  vec3 dir = normalize(vec3(uv.x * FOV, uv.y * FOV - PITCH, 1.0));
  // gentle camera bob, like standing on a shore
  ori.y += sin(u_time * 0.5) * 0.05;
  dir.y += cos(u_time * 0.33) * 0.004;
  dir = normalize(dir);

  vec3 color;
  if (dir.y > 0.045) {
    color = skyColor(dir);
  } else {
    vec3 p;
    heightMapTracing(ori, dir, p);
    float dist = length(p - ori);
    float eps = clamp(dist * 0.12 / u_res.x, 0.004, 0.08);
    vec3 n = getNormal(p, eps);
    vec3 sea = seaColor(p, n, ori, dir, dist);
    // fog the far sea into the horizon sky
    vec3 horizonSky = skyColor(normalize(vec3(dir.x, 0.02, dir.z)));
    float fog = smoothstep(18.0, 58.0, dist);
    color = mix(sea, horizonSky, fog);
    // stitch the waterline
    color = mix(color, horizonSky, smoothstep(0.045, 0.02, -dir.y));
  }

  // filmic-ish tone-map + vignette + grain
  color = color / (color + vec3(0.85));
  vec2 q = gl_FragCoord.xy / u_res.xy - 0.5;
  color *= 1.0 - dot(q, q) * mix(0.45, 0.56, u_hero);
  color += (hash(gl_FragCoord.xy + fract(u_time) * 61.0) - 0.5) * 0.012;
  float exposure = mix(1.38, 1.30 + u_intensity * 0.18 + u_theme * 0.10, u_hero);
  gl_FragColor = vec4(pow(color * exposure, vec3(0.90)), 1.0);
}`;

const SIM_N = 224;
const MAX_DPR = 1.5;
// world patch the ripple sim covers (must match the JS ray math below)
const PATCH = { x0: -11, z0: 1.5, w: 22, d: 20 };
const CAMY = 3.4, PITCH = 0.16, FOV = 0.75;

type OceanTheme = "light" | "dark";

export interface OceanFieldProps {
  className?: string;
  theme?: OceanTheme;
  hue?: number;
  intensity?: number;
  amplitude?: number;
  reducedMotion?: boolean;
  contained?: boolean;
  interactive?: boolean;
  targetFps?: number;
  onReady?: () => void;
  onUnavailable?: () => void;
}

function clamp01(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || Number.isNaN(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

export function OceanField({
  className,
  theme = "dark",
  hue,
  intensity,
  amplitude,
  reducedMotion = false,
  contained = false,
  interactive: interactiveProp = true,
  targetFps = 60,
  onReady,
  onUnavailable,
}: OceanFieldProps = {}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const onReadyRef = useRef(onReady);
  const onUnavailableRef = useRef(onUnavailable);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);
  useEffect(() => {
    onUnavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  useEffect(() => {
    const canvas = ref.current!;
    const perf = getPerf();
    if (contained && perf.tier === "low") {
      onUnavailableRef.current?.();
      return undefined;
    }
    const reduced =
      reducedMotion ||
      perf.reducedMotion ||
      document.documentElement.dataset.reducedmotion === "1" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const forceFlat = typeof location !== "undefined" && location.search.includes("flat");
    const shotMode = typeof location !== "undefined" && location.search.includes("shot=1");
    let cleanupPointers = () => {};

    let gl: WebGL2RenderingContext | null = null;
    let interactive = false;
    if (!forceFlat) {
      gl = canvas.getContext("webgl2", { antialias: false, alpha: false, powerPreference: "high-performance", preserveDrawingBuffer: shotMode });
      if (gl && gl.getExtension("EXT_color_buffer_float")) interactive = interactiveProp && !reduced;
    }

    if (!gl) {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(2, Math.floor((contained ? rect.width : window.innerWidth) || window.innerWidth));
      canvas.height = Math.max(2, Math.floor((contained ? rect.height : window.innerHeight) || window.innerHeight));
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      const c2d = canvas.getContext("2d");
      if (c2d) {
        const H = canvas.height, W = canvas.width;
        const sky = c2d.createLinearGradient(0, 0, 0, H * 0.5);
        sky.addColorStop(0, "#070305");
        sky.addColorStop(1, "#4E101D");
        c2d.fillStyle = sky;
        c2d.fillRect(0, 0, W, H * 0.5);
        const sea = c2d.createLinearGradient(0, H * 0.5, 0, H);
        sea.addColorStop(0, "#A02C44");
        sea.addColorStop(0.3, "#8C1F2F");
        sea.addColorStop(1, "#4E101D");
        c2d.fillStyle = sea;
        c2d.fillRect(0, H * 0.5, W, H * 0.5);
      }
      onUnavailableRef.current?.();
      return;
    }

    const compile = (type: number, src: string) => {
      const sh = gl!.createShader(type)!;
      gl!.shaderSource(sh, src);
      gl!.compileShader(sh);
      if (!gl!.getShaderParameter(sh, gl!.COMPILE_STATUS)) {
        console.error("[cadence] shader compile:", gl!.getShaderInfoLog(sh));
      }
      return sh;
    };
    const makeProgram = (frag: string) => {
      const p = gl!.createProgram()!;
      gl!.attachShader(p, compile(gl!.VERTEX_SHADER, VERT));
      gl!.attachShader(p, compile(gl!.FRAGMENT_SHADER, frag));
      gl!.linkProgram(p);
      return p;
    };

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

    const oceanProg = makeProgram(OCEAN_FRAG);
    const bindAttribs = (p: WebGLProgram) => {
      gl!.useProgram(p);
      const loc = gl!.getAttribLocation(p, "a");
      gl!.enableVertexAttribArray(loc);
      gl!.vertexAttribPointer(loc, 2, gl!.FLOAT, false, 0, 0);
    };

    // Ripple sim targets.
    let simProg: WebGLProgram | null = null;
    let texA: WebGLTexture | null = null, texB: WebGLTexture | null = null;
    let fbA: WebGLFramebuffer | null = null, fbB: WebGLFramebuffer | null = null;
    const impulses: { x: number; y: number; r: number; s: number }[] = [];
    let sImp: WebGLUniformLocation | null = null;
    const flatRipple = gl.createTexture();
    if (flatRipple) {
      gl.bindTexture(gl.TEXTURE_2D, flatRipple);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }

    if (interactive) {
      simProg = makeProgram(SIM_FRAG);
      const makeTarget = () => {
        const tex = gl!.createTexture()!;
        gl!.bindTexture(gl!.TEXTURE_2D, tex);
        gl!.texImage2D(gl!.TEXTURE_2D, 0, gl!.RG16F, SIM_N, SIM_N, 0, gl!.RG, gl!.HALF_FLOAT, null);
        gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_MIN_FILTER, gl!.LINEAR);
        gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_MAG_FILTER, gl!.LINEAR);
        gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_WRAP_S, gl!.CLAMP_TO_EDGE);
        gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_WRAP_T, gl!.CLAMP_TO_EDGE);
        const fb = gl!.createFramebuffer()!;
        gl!.bindFramebuffer(gl!.FRAMEBUFFER, fb);
        gl!.framebufferTexture2D(gl!.FRAMEBUFFER, gl!.COLOR_ATTACHMENT0, gl!.TEXTURE_2D, tex, 0);
        return { tex, fb };
      };
      const A = makeTarget(), B = makeTarget();
      texA = A.tex; fbA = A.fb; texB = B.tex; fbB = B.fb;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);

      gl.useProgram(simProg);
      gl.uniform1i(gl.getUniformLocation(simProg, "u_prev"), 0);
      gl.uniform2f(gl.getUniformLocation(simProg, "u_texel"), 1 / SIM_N, 1 / SIM_N);
      sImp = gl.getUniformLocation(simProg, "u_imp");

      // Pointer ray intersects the sea plane (y ~= 0.6) to world xz, then sim uv.
      // Mirrors the shader camera EXACTLY.
      const project = (clientX: number, clientY: number): { u: number; v: number } | null => {
        const rect = canvas.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return null;
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
        const ux = (x / rect.width) * 2 - 1;
        const uy = (1 - y / rect.height) * 2 - 1;
        const a = rect.width / rect.height;
        const dir = [ux * a * FOV, uy * FOV - PITCH, 1];
        const len = Math.hypot(dir[0], dir[1], dir[2]);
        dir[0] /= len; dir[1] /= len; dir[2] /= len;
        if (dir[1] > -0.02) return null; // pointing at the sky
        const t = (0.6 - CAMY) / dir[1];
        const worldX = dir[0] * t, worldZ = dir[2] * t;
        const u = (worldX - PATCH.x0) / PATCH.w;
        const v = (worldZ - PATCH.z0) / PATCH.d;
        if (u < 0.02 || u > 0.98 || v < 0.02 || v > 0.98) return null;
        return { u, v };
      };
      const push = (clientX: number, clientY: number, strength: number) => {
        const p = project(clientX, clientY);
        if (p && impulses.length < 8) impulses.push({ x: p.u, y: p.v, r: 0.00012, s: strength });
      };
      let lastMove = 0;
      const onMove = (e: PointerEvent) => {
        const now = performance.now();
        if (now - lastMove < 28) return;
        lastMove = now;
        push(e.clientX, e.clientY, e.buttons ? 1.4 : 0.5);
      };
      const onDown = (e: PointerEvent) => push(e.clientX, e.clientY, 2.6);
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerdown", onDown, { passive: true });
      cleanupPointers = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerdown", onDown);
      };
    }

    // ocean uniforms
    bindAttribs(oceanProg);
    const u = (n: string) => gl!.getUniformLocation(oceanProg, n);
    const uRes = u("u_res"), uTime = u("u_time"), uQ = u("u_quality"), uRip = u("u_ripple"), uPatch = u("u_patch");
    const uHero = u("u_hero"), uTheme = u("u_theme"), uHue = u("u_hue"), uIntensity = u("u_intensity"), uAmplitude = u("u_amplitude");
    const tier = getPerf().tier;
    gl.uniform1f(uQ, tier === "high" ? 1 : tier === "medium" ? 0.6 : 0.3);
    gl.uniform4f(uPatch, PATCH.x0, PATCH.z0, PATCH.w, PATCH.d);
    gl.uniform1i(uRip, 0);
    gl.uniform1f(uHero, contained ? 1 : 0);
    gl.uniform1f(uTheme, theme === "light" ? 1 : 0);
    gl.uniform1f(uHue, clamp01(hue, 0.55));
    gl.uniform1f(uIntensity, clamp01(intensity, 0.84));
    gl.uniform1f(uAmplitude, clamp01(amplitude, 1.0));

    let currentTier = tier;
    let scale = contained
      ? Math.min(0.82, AURORA_SCALE[currentTier] + 0.08)
      : Math.min(AURORA_SCALE[currentTier] + 0.05, 1 / (window.devicePixelRatio || 1) + 0.25);
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const cssW = contained ? (rect.width || window.innerWidth) : window.innerWidth;
      const cssH = contained ? (rect.height || window.innerHeight) : window.innerHeight;
      const dpr = contained ? Math.min(window.devicePixelRatio || 1, MAX_DPR) : 1;
      const w = Math.max(2, Math.floor(cssW * dpr * scale));
      const h = Math.max(2, Math.floor(cssH * dpr * scale));
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
    };
    resize();
    window.addEventListener("resize", resize);
    const ro = contained && "ResizeObserver" in window ? new ResizeObserver(resize) : null;
    ro?.observe(canvas);
    const unsubscribePerf = onPerf((next) => {
      if (contained && next.tier === "low") {
        onUnavailableRef.current?.();
        return;
      }
      currentTier = next.tier;
      scale = contained
        ? Math.min(0.82, AURORA_SCALE[currentTier] + 0.08)
        : Math.min(AURORA_SCALE[currentTier] + 0.05, 1 / (window.devicePixelRatio || 1) + 0.25);
      resize();
      bindAttribs(oceanProg);
      gl!.uniform1f(uQ, currentTier === "high" ? 1 : currentTier === "medium" ? 0.6 : 0.3);
    });

    let visible = true;
    const intersection = "IntersectionObserver" in window
      ? new IntersectionObserver(
          ([entry]) => {
            visible = entry?.isIntersecting ?? true;
            schedule();
          },
          { threshold: 0.01 },
        )
      : null;
    intersection?.observe(canvas);

    // ambient life: occasional soft droplets, like the first spots of rain
    let lastDrop = 0;
    const ambient = (now: number) => {
      if (!interactive || now - lastDrop < 4200) return;
      lastDrop = now;
      impulses.push({ x: 0.2 + Math.random() * 0.6, y: 0.15 + Math.random() * 0.5, r: 0.00010, s: 1.1 });
    };

    const impArray = new Float32Array(32);
    let ping = true;
    let raf = 0;
    let slow = 0;
    let last = performance.now();
    let lastFrame = 0;
    let queued = false;
    let alive = true;
    const start = last;
    let reportedReady = false;
    const targetFrameMs = Math.max(16, 1000 / Math.max(1, targetFps));

    const markReadyIfGood = () => {
      if (reportedReady || !onReadyRef.current || canvas.width <= 2 || canvas.height <= 2) return;
      const pixels = new Uint8Array(4 * 9);
      const x = Math.max(0, Math.floor(canvas.width * 0.5) - 1);
      const y = Math.max(0, Math.floor(canvas.height * 0.55) - 1);
      gl!.readPixels(x, y, 3, 3, gl!.RGBA, gl!.UNSIGNED_BYTE, pixels);
      let min = 255;
      let max = 0;
      let sum = 0;
      for (let i = 0; i < pixels.length; i += 4) {
        const lum = pixels[i]! * 0.2126 + pixels[i + 1]! * 0.7152 + pixels[i + 2]! * 0.0722;
        min = Math.min(min, lum);
        max = Math.max(max, lum);
        sum += lum;
      }
      const avg = sum / 9;
      if (avg > 10 && max - min > 1.5) {
        reportedReady = true;
        canvas.closest<HTMLElement>(".page-hero")?.setAttribute("data-hero-ready", "true");
        onReadyRef.current();
      }
    };

    const render = (now: number, still = false) => {
      resize();
      if (!still && now - last > 34) { slow++; if (slow > 30 && scale > 0.3) { scale = Math.max(0.3, scale - 0.08); resize(); slow = 0; } }
      else slow = Math.max(0, slow - 1);
      last = now;

      ambient(now);
      if (interactive && simProg) {
        impArray.fill(0);
        for (let i = 0; i < Math.min(8, impulses.length); i++) {
          const im = impulses[i];
          impArray[i * 4] = im.x; impArray[i * 4 + 1] = im.y; impArray[i * 4 + 2] = im.r; impArray[i * 4 + 3] = im.s;
        }
        impulses.length = 0;
        bindAttribs(simProg);
        gl!.uniform4fv(sImp, impArray);
        gl!.viewport(0, 0, SIM_N, SIM_N);
        gl!.bindFramebuffer(gl!.FRAMEBUFFER, ping ? fbB : fbA);
        gl!.activeTexture(gl!.TEXTURE0);
        gl!.bindTexture(gl!.TEXTURE_2D, ping ? texA! : texB!);
        gl!.drawArrays(gl!.TRIANGLES, 0, 3);
        gl!.bindFramebuffer(gl!.FRAMEBUFFER, null);
        ping = !ping;
      }

      bindAttribs(oceanProg);
      gl!.viewport(0, 0, canvas.width, canvas.height);
      gl!.uniform2f(uRes, canvas.width, canvas.height);
      gl!.uniform1f(uTime, reduced || still ? 8 : (now - start) / 1000);
      gl!.activeTexture(gl!.TEXTURE0);
      gl!.bindTexture(gl!.TEXTURE_2D, interactive ? (ping ? texA! : texB!) : flatRipple);
      gl!.drawArrays(gl!.TRIANGLES, 0, 3);
      markReadyIfGood();
    };

    const loop = () => {
      queued = false;
      if (!alive) return;
      const now = performance.now();
      if (visible && !document.hidden && now - lastFrame >= targetFrameMs) {
        lastFrame = now;
        render(now);
      }
      schedule();
    };

    function schedule(): void {
      if (!alive || reduced || queued || !visible || document.hidden) return;
      queued = true;
      raf = requestAnimationFrame(loop);
    }

    const onVisibility = () => {
      if (!document.hidden) {
        last = performance.now();
        schedule();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    render(performance.now(), true);
    if (!reduced) schedule();

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      ro?.disconnect();
      unsubscribePerf();
      intersection?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      cleanupPointers();
      /* context is GC'd with its canvas; explicit loseContext breaks StrictMode remounts */
    };
  }, [amplitude, contained, hue, intensity, interactiveProp, reducedMotion, targetFps, theme]);

  return (
    <canvas
      ref={ref}
      className={className}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        background:
          "linear-gradient(180deg, #070305 0%, #4E101D 42%, #8C1F2F 52%, #4E101D 100%)",
      }}
    />
  );
}

export default OceanField;
